import axiosInstance from "../utils/axiosInstance";

/**
 * planBoardApi: the v6 plan board routes (master plan section 5.2), and
 * nothing else. Every call goes through axiosInstance.
 *
 * Every function resolves to the response's `data` object, or throws a
 * PlanBoardApiError. The error carries what the store needs to decide what to
 * do next, and nothing about axios:
 *   status     the HTTP status, or null when no response arrived
 *   code       the refusal code (`task_already_claimed`, `level_submitted`...)
 *   details    the rest of the refusal body (`winning_task`, `assignee_user_id`,
 *              `board_version`...)
 *   transient  true when retrying the SAME request could succeed (no response,
 *              a 5xx that is not the read-only pause, 429)
 *
 * ENVELOPES (the real B1 backend; contract correction 2026-09-26). A success
 * is `{ success: true, data: {...} }`. A refusal is
 * `{ success: false, message: <reason code>, errors: <details>, board_version,
 * server_now }`: the reason code IS the message (`task_already_claimed`), and
 * the details (`assignee_user_id`, `winning_task`, or a validation field map)
 * are under `errors`. A message that is not a code (a Laravel validation
 * sentence) leaves the code as `http_<status>`, with the field map in details.
 *
 * DELETE bodies: axiosInstance sends GET and DELETE as `(url, config)`, so a
 * DELETE cannot carry a JSON body. The optimistic-lock `version` for the two
 * delete routes travels as a query parameter instead.
 */

const base = (sessionId) => `/plan-session/${sessionId}/v6`;

export class PlanBoardApiError extends Error {
  constructor({ status, code, details, message, transient }) {
    super(message || code || "plan_board_request_failed");
    this.name = "PlanBoardApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.transient = transient;
  }
}

const PAUSED = "plan_board_v6_paused";

export function toPlanBoardError(error) {
  if (error instanceof PlanBoardApiError) return error;
  const response = error?.response;
  if (!response) {
    return new PlanBoardApiError({
      status: null,
      code: "network_error",
      details: {},
      message: error?.message || "network_error",
      transient: true,
    });
  }
  const body =
    response.data && typeof response.data === "object" ? response.data : {};
  const errors =
    body.errors &&
    typeof body.errors === "object" &&
    !Array.isArray(body.errors)
      ? body.errors
      : {};
  const isCode =
    typeof body.message === "string" && /^[a-z0-9_]+$/.test(body.message);
  const code = isCode ? body.message : `http_${response.status}`;
  const status = response.status;
  const transient =
    status === 429 || (status >= 500 && status !== 501 && code !== PAUSED);
  const details = { ...errors };
  if (body.board_version !== undefined)
    details.board_version = body.board_version;
  if (body.server_now !== undefined) details.server_now = body.server_now;
  return new PlanBoardApiError({
    status,
    code,
    details,
    message: typeof body.message === "string" ? body.message : code,
    transient,
  });
}

const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === "object" && body.data !== undefined) {
    return body.data;
  }
  return body;
};

/**
 * Every v6 request gives up after this long (PR #464 review). A request the
 * server accepted and never answered would otherwise hang: a snapshot would
 * hold the board's fetch in flight (events buffered, no reconcile), and a
 * write would stay "saving". A timeout rejects with no response, so it is a
 * transient network_error: the fetch clears and a gap refetch can recover,
 * and a keyed write retries with its key (the server replays it).
 */
export const REQUEST_TIMEOUT_MS = 15000;

async function call(url, method, data, config) {
  try {
    const response = await axiosInstance(url, method, data, {
      timeout: REQUEST_TIMEOUT_MS,
      ...(config || {}),
    });
    return unwrap(response);
  } catch (error) {
    throw toPlanBoardError(error);
  }
}

/* ---------------- snapshot ---------------- */

export const getBoard = (sessionId, { signal } = {}) =>
  call(`${base(sessionId)}/board`, "get", undefined, signal ? { signal } : {});

/* ---------------- ideas ---------------- */

export const addIdea = (sessionId, { text, requestKey }) =>
  call(`${base(sessionId)}/ideas`, "post", {
    text,
    client_request_id: requestKey,
  });

export const editIdea = (sessionId, { ideaId, text, version }) =>
  call(`${base(sessionId)}/ideas/${ideaId}`, "patch", { text, version });

export const deleteIdea = (sessionId, { ideaId, version }) =>
  call(`${base(sessionId)}/ideas/${ideaId}`, "delete", undefined, {
    params: { version },
  });

export const convertIdea = (
  sessionId,
  { ideaId, title, another, requestKey },
) =>
  call(`${base(sessionId)}/ideas/${ideaId}/convert`, "post", {
    title,
    another: Boolean(another),
    client_request_id: requestKey,
  });

/* ---------------- tasks ---------------- */

export const addTask = (sessionId, { title, requestKey }) =>
  call(`${base(sessionId)}/tasks`, "post", {
    title,
    client_request_id: requestKey,
  });

export const editTask = (sessionId, { taskId, title, version }) =>
  call(`${base(sessionId)}/tasks/${taskId}`, "patch", { title, version });

export const deleteTask = (sessionId, { taskId, version }) =>
  call(`${base(sessionId)}/tasks/${taskId}`, "delete", undefined, {
    params: { version },
  });

export const restoreTask = (sessionId, { taskId, requestKey }) =>
  call(`${base(sessionId)}/tasks/${taskId}/restore`, "post", {
    client_request_id: requestKey,
  });

export const claimTask = (sessionId, { taskId, requestKey }) =>
  call(`${base(sessionId)}/tasks/${taskId}/claim`, "post", {
    client_request_id: requestKey,
  });

export const releaseTask = (sessionId, { taskId, requestKey }) =>
  call(`${base(sessionId)}/tasks/${taskId}/release`, "post", {
    client_request_id: requestKey,
  });

/* ---------------- definition (B2) ---------------- */

/**
 * The viewer's own definition draft for a task they hold (D1; the real B2
 * route). `deltaSeconds` is the define time since this student's last save
 * (0 to 3600); the server adds it to the draft's `accumulated_seconds`.
 * `version` is the draft's current version, 1 before the first save; a stale
 * one answers 409 stale_version. The response is `{ definition_draft }`.
 */
export const saveDefinitionDraft = (
  sessionId,
  { taskId, fields, deltaSeconds, version },
) =>
  call(`${base(sessionId)}/tasks/${taskId}/definition-draft`, "put", {
    task_why: fields.why ?? null,
    task_done: fields.done ?? null,
    task_approach: fields.approach ?? null,
    delta_seconds: Math.min(3600, Math.max(0, Math.round(deltaSeconds || 0))),
    version: version ?? 1,
  });

/** Promote (lock) one task with its definition. Keyed (5.2). */
export const finalizeTask = (
  sessionId,
  {
    taskId,
    why,
    done,
    approach,
    milestoneId,
    creationDurationSeconds,
    requestKey,
  },
) =>
  call(`${base(sessionId)}/tasks/${taskId}/finalize`, "post", {
    task_why: why,
    task_done: done,
    task_approach: approach,
    milestone_id: milestoneId,
    creation_duration_seconds: creationDurationSeconds,
    client_request_id: requestKey,
  });

/* ---------------- round and phase ---------------- */

export const startRound = (sessionId) =>
  call(`${base(sessionId)}/round/start`, "post", {});

export const skipRound = (sessionId) =>
  call(`${base(sessionId)}/round/skip`, "post", {});

export const openClaim = (sessionId) =>
  call(`${base(sessionId)}/phase`, "post", { phase: "claim" });

/**
 * The legacy session read (unchanged route, passed through by the legacy
 * guard): the only way to learn the session id from a squad and a capsule,
 * which the v6 snapshot route needs. It is a firstOrCreate on the server: it
 * can create the squad's planning session for this level if none exists
 * yet, exactly as opening the legacy board always has.
 */
export const getLegacySession = (squadId, capsuleId, { signal } = {}) =>
  call(
    `/plan-session/${squadId}/${capsuleId}`,
    "get",
    undefined,
    signal ? { signal } : {},
  );
